import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import * as RestApi from 'core/network/RestApi';
import { useQuery } from 'react-query';
import { generatePath } from 'react-router';
import { ITemplate } from 'entities/template/template';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../context/useRcsTemplateEditorContext';
import { templateBodyToRcsConfig } from '../utils/rcsConfigToCardContent';

/**
 * When previewingVersion is set, fetches that version's template and updates
 * rcsConfig + readOnly in context so the form and preview show the version.
 * When previewingVersion is cleared, restores the previously saved config.
 */
export function useRcsPreviewVersion() {
  const { siteId, id: templateId, language } = useParams<{
    siteId: string;
    id: string;
    language: string;
  }>();
  const { previewingVersion, rcsConfig } = useRcsTemplateEditorState();
  const dispatch = useRcsTemplateEditorDispatch();
  const configBeforePreviewRef = useRef<typeof rcsConfig | null>(null);
  const latestRcsConfigRef = useRef(rcsConfig);
  latestRcsConfigRef.current = rcsConfig;

  const versionQuery = useQuery(
    ['template', siteId, templateId, language, previewingVersion],
    async () => {
      const result = await RestApi.get<ITemplate>(
        generatePath(TemplateApiEndpoints.GET_TEMPLATE_VERSION, {
          siteId,
          templateId,
          language,
          versionId: previewingVersion,
        }),
      );
      return result.data;
    },
    { enabled: Boolean(siteId && templateId && language && previewingVersion) },
  );

  useEffect(() => {
    if (previewingVersion) {
      if (versionQuery.isSuccess && versionQuery.data && configBeforePreviewRef.current === null) {
        const current = latestRcsConfigRef.current;
        configBeforePreviewRef.current = { ...current, actions: current.actions.map(a => ({ ...a })) };
      }
      if (versionQuery.isSuccess && versionQuery.data) {
        const config = templateBodyToRcsConfig(versionQuery.data.template);
        if (config !== null) {
          dispatch({
            type: 'SET_RCS_CONFIG',
            payload: { ...config, hasUnsavedChanges: false },
          });
          dispatch({
            type: 'SET_EDITOR_SETTINGS',
            payload: { readOnly: true },
          });
        }
      }
    } else {
      const saved = configBeforePreviewRef.current;
      if (saved) {
        dispatch({
          type: 'SET_RCS_CONFIG',
          payload: { ...saved, hasUnsavedChanges: false },
        });
        dispatch({ type: 'SET_EDITOR_SETTINGS', payload: { readOnly: false } });
        configBeforePreviewRef.current = null;
      }
    }
  }, [versionQuery.isSuccess, versionQuery.data, previewingVersion, dispatch]);
}
